import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  AlertCircle,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  History,
  Info,
  KeyRound,
  Lock,
  Save,
  User,
} from "lucide-react";
import { toast } from "react-toastify";
import { apiClient } from "@/services/apiClient";
import { profileService, LegalEntityHierarchy } from "@/services/profileService";
import {
  crmIntegrationConfigsService,
  type CrmIntegrationConfigRecord,
} from "@/services/crmIntegrationConfigsService";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  fetchBranchEntityStatuses,
  aggregateBranchStatus,
  PROPCO_HISTORICAL_SYNC_ENTITY_TYPES,
} from "@/services/crmGatewaySyncService";
import { HistoricalSyncModal } from "./HistoricalSyncModal";

const CRM_ID = "PROPCO";
/** Display name — the route carries the uppercase code. */
const CRM_LABEL = "Propco";
/** One account covers the whole group, so the config binds at the legal entity. */
const CONFIG_LEVEL = "LEGAL_ENTITY";
/** Shown as the tooltip and hint once the credentials are saved. */
const LOCKED_HINT = "Propco credentials can't be changed once connected";
/** Stand-in for the saved password — the API never returns it. */
const PASSWORD_MASK = "••••••••••••";
/** While the group is in one of these, a fresh historical sync can't be started. */
const ACTIVE_HISTORICAL_SYNC_STATUSES = new Set(["IN_PROGRESS", "RUNNING", "QUEUED"]);

interface Props {
  onBack: () => void;
}

/**
 * Propco configuration. Same shape as the ref-mapped CRM screen, but there is
 * no hierarchy to map: Propco authenticates with a single account for the whole
 * group, so a single CRM integration config row keyed to the ROOT legal entity
 * covers every legal entity and branch beneath it.
 */
export const PropcoIntegrationDetail: React.FC<Props> = ({ onBack }) => {
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;

  const [rootEntity, setRootEntity] = useState<LegalEntityHierarchy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Baselines for the dirty check — what the server currently holds.
  const [savedUsername, setSavedUsername] = useState("");
  const [savedPassword, setSavedPassword] = useState("");
  const [hasSavedConfig, setHasSavedConfig] = useState(false);

  // Aggregate historical-sync status for the root legal entity — Propco has no
  // branches, so the whole group syncs as one target. Fetched once the group is
  // connected and kept fresh by the modal, exactly as the branch History icons
  // do on the ref-mapped CRM screen.
  const [historicalStatus, setHistoricalStatus] = useState<string | undefined>();
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  const connected = Boolean(hasSavedConfig && savedUsername.trim());

  /**
   * The CRM integration config is the source of truth — the credentials live
   * there and nowhere else. The API never reads the password back out, so a
   * saved config hydrates the field with the mask instead of the real value.
   */
  const hydrate = (le: LegalEntityHierarchy, config?: CrmIntegrationConfigRecord | null) => {
    setRootEntity(le);
    const nextUsername = config?.username ?? "";
    const nextPassword = config ? PASSWORD_MASK : "";
    setHasSavedConfig(Boolean(config));
    setUsername(nextUsername);
    setPassword(nextPassword);
    setSavedUsername(nextUsername);
    setSavedPassword(nextPassword);
  };

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    setLoading(true);
    void profileService
      .get(email)
      .then(async (p) => {
        const first = p.legalEntities[0];
        if (!first) throw new Error("No legal entity is available for this account.");
        // Propco is configured once per group, so the credentials always belong
        // to the root legal entity — a sub-entity would be unreachable.
        const rootId = await resolveRootLegalEntityId(first.id);
        const root = findEntity(p.legalEntities, rootId) ?? first;
        const config = await loadPropcoConfig(root.id);
        if (cancelled) return;
        hydrate(root, config);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load the Propco configuration.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  // Once connected there is something to replay, so pull the group's aggregate
  // status — it decides whether a fresh sync can be started.
  useEffect(() => {
    if (!connected || !rootEntity) return;
    let cancelled = false;
    void fetchBranchEntityStatuses([rootEntity.id], {
      entityTypes: PROPCO_HISTORICAL_SYNC_ENTITY_TYPES,
      scope: "LEGAL_ENTITY",
    })
      .then((byEntity) => {
        if (cancelled) return;
        setHistoricalStatus(
          aggregateBranchStatus(byEntity[rootEntity.id] ?? {}, PROPCO_HISTORICAL_SYNC_ENTITY_TYPES),
        );
      })
      .catch(() => {
        // Best-effort — the modal can still fetch on demand via Refresh.
      });
    return () => {
      cancelled = true;
    };
  }, [connected, rootEntity]);

  const historicalSyncRunning = ACTIVE_HISTORICAL_SYNC_STATUSES.has(
    String(historicalStatus ?? "").toUpperCase(),
  );

  // Credentials are write-once: once the group is connected they belong to the
  // live integration, so the fields go read-only rather than letting an operator
  // silently repoint every legal entity beneath the root.
  const locked = connected;

  const usernameMissing = !username.trim();
  const passwordMissing = !password.trim();
  const isDirty = username !== savedUsername || password !== savedPassword;
  const saveDisabled = locked || !rootEntity || usernameMissing || passwordMissing || !isDirty;

  const saveReason = locked
    ? LOCKED_HINT
    : !rootEntity
      ? "Loading your legal entity"
      : usernameMissing
        ? "Enter the Propco username"
        : passwordMissing
          ? "Enter the Propco password"
          : !isDirty
            ? "No changes to save"
            : "";

  async function handleSave() {
    if (!rootEntity || !email || saveDisabled) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Propco has no hierarchy to map, so the gateway config is a single row
      // on the root legal entity: no branch, no agency/branch refs, and the
      // tuning knobs left null so the server keeps its own defaults.
      await crmIntegrationConfigsService.bulkUpsert([
        {
          legalEntityId: rootEntity.id,
          branchId: null,
          configLevel: CONFIG_LEVEL,
          crmType: CRM_ID,
          agencyRef: null,
          externalBranchId: null,
          username: username.trim(),
          password,
          customerCode: null,
          isActive: true,
          pollingEnabled: false,
          pollingIntervalMinutes: null,
          pageSize: null,
          rateLimitPerMinute: null,
          concurrentRequests: null,
        },
      ]);

      useCrmMappingSyncStore.getState().bumpCrmMappingEpoch();

      // The config row is the only thing the save touched, so re-read it rather
      // than the profile — the legal entity is unchanged.
      const saved = await loadPropcoConfig(rootEntity.id);
      if (saved) {
        hydrate(rootEntity, saved);
      } else {
        // Save succeeded; worst case the operator sees stale state until they
        // manually refresh.
        setHasSavedConfig(true);
        setSavedUsername(username.trim());
        setSavedPassword(password);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      toast.success("Propco credentials saved.");
    } catch (err: unknown) {
      const res = err as { response?: { data?: { message?: string } }; message?: string };
      setError(res?.response?.data?.message ?? res?.message ?? "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bridge-accent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-bridge-bg">
      <header className="px-8 py-4 bg-white border-b border-bridge-border flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-bridge-bg rounded-full text-bridge-muted transition-colors"
            aria-label="Back to integrations"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="text-2xl">🔐</div>
            <div>
              <h1 className="text-xl font-bold text-bridge-fg">{CRM_LABEL} Configuration</h1>
              <p className="text-xs text-bridge-muted mt-0.5">
                One {CRM_LABEL} account connects every legal entity and branch.
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={saveDisabled}
          title={saveReason || undefined}
          className="bg-bridge-accent hover:bg-bridge-accent/90 text-white rounded-xl px-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Configuration
        </Button>
      </header>

      <main className="flex-1 overflow-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-2xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-green-50 border border-green-100 text-green-600 text-sm rounded-2xl flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              Configuration saved successfully!
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 text-blue-800 text-sm">
            <Info className="w-5 h-5 shrink-0 text-blue-400" />
            <div className="space-y-1">
              <p>
                Enter the <strong>Username</strong> and <strong>Password</strong> of your Propco
                account. There are no per-branch references to map — one account covers the whole
                group, so the credentials are saved on your root legal entity.
              </p>
              <p>
                Once saved, every legal entity and branch beneath it is linked to {CRM_LABEL}.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-bridge-border shadow-sm overflow-hidden">
            <div className="px-8 py-4 bg-slate-50 border-b border-bridge-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-bridge-accent" />
                <span className="font-bold text-bridge-fg">{rootEntity?.name ?? "Root Legal Entity"}</span>
              </div>
              {connected ? (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold border border-green-100">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Connected
                  </span>
                  {/* Propco has no branches, so the historical sync hangs off the
                      legal entity itself — one button for the whole group. */}
                  <Tooltip
                    side="left"
                    content={
                      historicalSyncRunning
                        ? "Syncing in progress"
                        : "Sync historical data for this legal entity"
                    }
                  >
                    <span className="inline-flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => setSyncModalOpen(true)}
                        disabled={historicalSyncRunning}
                        aria-label={`Sync historical data for ${rootEntity?.name ?? "root legal entity"}`}
                        className={`w-9 h-9 inline-flex items-center justify-center rounded-full border border-bridge-border bg-white text-bridge-accent transition-colors ${
                          historicalSyncRunning
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:bg-bridge-accent hover:text-white hover:border-bridge-accent"
                        }`}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    </span>
                  </Tooltip>
                </div>
              ) : (
                <span className="px-2.5 py-1 rounded-full bg-slate-200/80 text-[10px] font-bold uppercase tracking-wide text-slate-500 border border-slate-200">
                  Not connected
                </span>
              )}
            </div>

            <div className="px-8 py-7 space-y-6" title={locked ? LOCKED_HINT : undefined}>
              {/* One row per credential, same as the ref-mapped CRM screen: the
                  label holds the left edge, the field the right. */}
              <div className="flex items-center justify-between gap-6">
                <label
                  htmlFor="propco-username"
                  className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap shrink-0"
                >
                  Username <span className="text-red-500">*</span>
                </label>
                <Input
                  id="propco-username"
                  autoComplete="off"
                  leading={<User className="w-4 h-4" />}
                  placeholder="Propco username"
                  className={lockedFieldClass(locked)}
                  title={locked ? LOCKED_HINT : undefined}
                  readOnly={locked}
                  aria-readonly={locked || undefined}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  trailing={locked ? <Lock className="w-3.5 h-3.5" /> : undefined}
                />
              </div>

              <div className="flex items-center justify-between gap-6">
                <label
                  htmlFor="propco-password"
                  className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap shrink-0"
                >
                  Password <span className="text-red-500">*</span>
                </label>
                <Input
                  id="propco-password"
                  // Locked fields never reveal: the value on screen is a mask,
                  // not the stored password, so there is nothing to show.
                  type={!locked && showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  leading={<KeyRound className="w-4 h-4" />}
                  placeholder="Propco password"
                  className={lockedFieldClass(locked)}
                  title={locked ? LOCKED_HINT : undefined}
                  readOnly={locked}
                  aria-readonly={locked || undefined}
                  value={locked ? PASSWORD_MASK : password}
                  onChange={(e) => setPassword(e.target.value)}
                  trailing={
                    locked ? (
                      <Lock className="w-3.5 h-3.5" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="text-bridge-muted hover:text-bridge-fg transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )
                  }
                />
              </div>

              {saveReason && <p className="pt-1 text-[11px] text-bridge-muted">{saveReason}.</p>}
            </div>
          </div>
        </div>
      </main>

      <HistoricalSyncModal
        open={syncModalOpen}
        branchId={rootEntity?.id ?? ""}
        branchName={rootEntity?.name ?? "Root Legal Entity"}
        entityTypes={PROPCO_HISTORICAL_SYNC_ENTITY_TYPES}
        scope="LEGAL_ENTITY"
        targetLabel="Legal Entity"
        status={historicalStatus}
        onStatusChange={(_id, status) => setHistoricalStatus(status.toUpperCase())}
        onClose={() => setSyncModalOpen(false)}
      />
    </div>
  );
};

/**
 * Same lookup the ref-mapped CRM screen uses to tell whether it is sitting on
 * the root of the hierarchy. Falls back to the given id if the call fails.
 */
async function resolveRootLegalEntityId(legalEntityId: string): Promise<string> {
  try {
    const res = await apiClient.get<{ rootLegalEntityId?: string }>(
      "/profile/hierarchy/root-legal-entity",
      { params: { legalEntityId } },
    );
    return res.data?.rootLegalEntityId?.trim() || legalEntityId;
  } catch {
    return legalEntityId;
  }
}

/** Locked fields keep their shape but read as inert: muted, no caret, no focus ring. */
function lockedFieldClass(locked: boolean): string {
  // Three quarters of the row, pinned to its right edge by the row's
  // justify-between — the label holds the left edge.
  const base = "h-10 w-3/4 shrink-0 shadow-sm";
  return locked
    ? `${base} bg-slate-50 border-bridge-border/70 text-bridge-muted cursor-not-allowed focus-within:border-bridge-border/70 focus-within:shadow-sm [&_input]:cursor-not-allowed [&_input]:text-bridge-muted`
    : `${base} bg-white`;
}

/**
 * The saved Propco config for a legal entity, or null when there is none yet.
 * A failure here is not fatal — the screen still renders off the legal entity.
 */
async function loadPropcoConfig(legalEntityId: string): Promise<CrmIntegrationConfigRecord | null> {
  try {
    const configs = await crmIntegrationConfigsService.list(legalEntityId);
    return configs.find((c) => c.crmType?.toUpperCase() === CRM_ID) ?? null;
  } catch {
    return null;
  }
}

function findEntity(entities: LegalEntityHierarchy[], id: string): LegalEntityHierarchy | null {
  for (const entity of entities) {
    if (entity.id === id) return entity;
    if (entity.subEntities) {
      const found = findEntity(entity.subEntities, id);
      if (found) return found;
    }
  }
  return null;
}
